// Preview bridge — admin-builder ↔ storefront-iframe channel.
//
// SECURITY (N23 + N24):
//   - The entire bridge is gated behind `import.meta.env.DEV`. In prod
//     builds, `initPreviewBridge` and `getStoredPreviewConfig` short-
//     circuit with no listeners registered. The prod bundle therefore
//     contains no live `postMessage` handler that could be coerced by a
//     malicious page on the same browser; tree-shake removes the
//     dev-only branches when Vite sees `import.meta.env.DEV === false`.
//   - All writes to `window.__STOREFRONT_CONFIG__` have been removed.
//     Preview payloads flow through the `divi:preview-config` CustomEvent;
//     TenantContext is the only consumer and stores the payload in React
//     state.
//   - sessionStorage is retained as a same-tab persistence layer so a
//     route change inside the iframe doesn't lose the last config, but
//     it is READ exclusively via `getStoredPreviewConfig()` — no global
//     window object is touched.

const PREVIEW_QUERY_FLAG = '__diviPreview';
const PREVIEW_FRESH_FLAG = '__diviPreviewFresh';
const PREVIEW_STORAGE_KEY = '__DIVI_PREVIEW_CONFIG__';
const PREVIEW_TIMESTAMP_KEY = '__DIVI_PREVIEW_TS__';
const PREVIEW_HASH_KEY = '__DIVI_PREVIEW_HASH__';

const hashPayload = (payload: unknown): string => {
  try {
    const str = JSON.stringify(payload) || '';
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = (hash << 5) - hash + str.charCodeAt(i);
      hash |= 0;
    }
    return String(hash);
  } catch {
    return '';
  }
};

// Prod allow-list = builder admin origins only. The dev ports
// (localhost:5173/5174/5175) are added INSIDE the dev-only branch in
// initPreviewBridge so they never appear in the prod bundle.
const ALLOWED_ADMIN_ORIGINS_PROD: string[] = [
  'https://app.chosenonearth.com',
  'https://agency.chosenonearth.com',
  'https://app.metatraderdemo.xyz',
  'https://app.blacksheepmission.net',
];

const isOriginAllowed = (origin: string, extra: string[]): boolean => {
  if (!origin) return false;
  if (ALLOWED_ADMIN_ORIGINS_PROD.includes(origin)) return true;
  if (extra.includes(origin)) return true;
  try {
    const { hostname } = new URL(origin);
    if (hostname.endsWith('.chosenonearth.com')) return true;
    if (hostname.endsWith('.metatraderdemo.xyz')) return true;
    if (hostname.endsWith('.blacksheepmission.net')) return true;
  } catch {
    /* ignore */
  }
  return false;
};

const getParams = (): URLSearchParams => {
  try {
    return new URLSearchParams(window.location.search);
  } catch {
    return new URLSearchParams();
  }
};

export const isPreviewMode = (): boolean => {
  if (typeof window === 'undefined') return false;
  return getParams().has(PREVIEW_QUERY_FLAG);
};

const isFreshMode = (): boolean => {
  if (typeof window === 'undefined') return false;
  return getParams().get(PREVIEW_FRESH_FLAG) === '1';
};

const clearStoredPreview = (): void => {
  try {
    sessionStorage.removeItem(PREVIEW_STORAGE_KEY);
    sessionStorage.removeItem(PREVIEW_TIMESTAMP_KEY);
    sessionStorage.removeItem(PREVIEW_HASH_KEY);
  } catch {
    /* ignore */
  }
};

/**
 * Read the last preview payload that the bridge wrote to sessionStorage.
 * Used by TenantContext on mount to rehydrate after a navigation inside
 * the iframe. Returns null in prod builds (bridge inactive) and on parse
 * failure.
 */
export const getStoredPreviewConfig = (): unknown | null => {
  if (!import.meta.env.DEV && !isPreviewMode()) return null;
  if (typeof window === 'undefined') return null;
  try {
    const stored = sessionStorage.getItem(PREVIEW_STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

const notifyReady = (): void => {
  try {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage({ type: 'DIVI_PREVIEW_READY' }, '*');
      // eslint-disable-next-line no-console
      console.log('[divi:preview-bridge] READY emitted to parent');
    } else {
      // eslint-disable-next-line no-console
      console.warn('[divi:preview-bridge] no parent window — skipping READY');
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[divi:preview-bridge] READY emit failed', e);
  }
};

export const initPreviewBridge = (): void => {
  // Audit 2026-05-21 — DEV-only gate removed (it broke prod live preview
  // in the admin builder because the bridge is what posts
  // DIVI_PREVIEW_READY back to the parent iframe). The bridge is now
  // gated on the URL flag instead — only active when the page was
  // loaded as `?__diviPreview=1`, which is something only the admin
  // builder sets when constructing the iframe src. Defence-in-depth:
  // the message listener below ALSO checks `isOriginAllowed` against
  // ALLOWED_ADMIN_ORIGINS_PROD before accepting any payload, so even
  // if a third-party page somehow set the flag, only messages from a
  // known admin origin can drive the bridge.
  if (typeof window === 'undefined') return;
  if (!isPreviewMode()) return;

  // Dev-only extra origins. Only assembled inside the DEV branch so
  // these strings are not present in the prod bundle.
  const extraDevOrigins = [
    'http://localhost:5173',
    'http://localhost:5174',
    'http://localhost:5175',
  ];

  // Fresh mode: template modal opens with no pre-baked config. Blow away
  // any stale sessionStorage so we never hydrate with another website's
  // payload.
  const fresh = isFreshMode();
  if (fresh) {
    clearStoredPreview();
  }

  let configReceived = false;
  let retryTimer: ReturnType<typeof setInterval> | null = null;

  window.addEventListener('message', (event: MessageEvent) => {
    const data = event.data;
    if (!data || typeof data !== 'object') return;

    // Scroll-to-section command from Builder
    if (data.type === 'DIVI_PREVIEW_SCROLL_TO_SECTION') {
      if (!isOriginAllowed(event.origin, extraDevOrigins)) return;
      const sectionId = data.sectionId;
      if (!sectionId) return;
      // Retry briefly if the section hasn't mounted yet (sections may
      // lazy-render on first config push).
      let attempts = 0;
      const tryScroll = () => {
        const el = document.querySelector(
          `[data-section-id="${CSS.escape(String(sectionId))}"]`,
        );
        if (el) {
          (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'start' });
          return true;
        }
        attempts += 1;
        if (attempts < 8) setTimeout(tryScroll, 150);
        return false;
      };
      tryScroll();
      return;
    }

    // Show-exit-intent command from Builder — lets a merchant preview the
    // exit-intent popup (which normally only fires when a real shopper tries
    // to leave). ExitIntentSection listens for the dispatched CustomEvent.
    if (data.type === 'DIVI_PREVIEW_SHOW_EXIT_INTENT') {
      if (!isOriginAllowed(event.origin, extraDevOrigins)) return;
      window.dispatchEvent(new CustomEvent('divi:preview-show-exit-intent'));
      return;
    }

    // Show-sticky-add-to-cart command from Builder — the bar is scroll-gated
    // (and dismissable), so this force-shows it for preview.
    if (data.type === 'DIVI_PREVIEW_SHOW_STICKY_ATC') {
      if (!isOriginAllowed(event.origin, extraDevOrigins)) return;
      window.dispatchEvent(new CustomEvent('divi:preview-show-sticky-atc'));
      return;
    }

    // Show-live-sales-notification command from Builder — the toast is on a
    // timer, so this force-shows it for preview.
    if (data.type === 'DIVI_PREVIEW_SHOW_LIVE_SALES') {
      if (!isOriginAllowed(event.origin, extraDevOrigins)) return;
      window.dispatchEvent(new CustomEvent('divi:preview-show-live-sales'));
      return;
    }

    if (data.type !== 'DIVI_PREVIEW_CONFIG') return;
    if (!isOriginAllowed(event.origin, extraDevOrigins)) {
      // eslint-disable-next-line no-console
      console.warn(
        '[divi:preview-bridge] rejected CONFIG from disallowed origin:',
        event.origin,
      );
      return;
    }
    const payload = data.payload;
    if (!payload || typeof payload !== 'object') return;
    // eslint-disable-next-line no-console
    console.log('[divi:preview-bridge] CONFIG received', {
      keys: Object.keys(payload).slice(0, 10),
    });

    configReceived = true;
    if (retryTimer) {
      clearInterval(retryTimer);
      retryTimer = null;
    }

    try {
      const incomingTs =
        typeof data.timestamp === 'number' ? data.timestamp : Date.now();
      const incomingHash = hashPayload(payload);
      const storedHash = sessionStorage.getItem(PREVIEW_HASH_KEY) || '';

      if (incomingHash && incomingHash === storedHash) {
        // Same payload — only re-dispatch the React event so any
        // late-mounting consumer catches up. No window-global write.
        window.dispatchEvent(
          new CustomEvent('divi:preview-config', { detail: payload }),
        );
        return;
      }

      sessionStorage.setItem(PREVIEW_STORAGE_KEY, JSON.stringify(payload));
      sessionStorage.setItem(PREVIEW_TIMESTAMP_KEY, String(incomingTs));
      sessionStorage.setItem(PREVIEW_HASH_KEY, incomingHash);
    } catch {
      /* ignore */
    }

    // React-bridge channel — TenantContext listens for this event and
    // stores the payload in context state. Window globals (the old
    // `__STOREFRONT_CONFIG__` write path) have been removed: every
    // unauditable script on the page used to be able to overwrite the
    // global, which gave a stored-XSS payload control over rendered
    // prices + sections.
    window.dispatchEvent(
      new CustomEvent('divi:preview-config', { detail: payload }),
    );
  });

  // Re-emit READY until parent sends a config — covers the race where
  // parent's message listener wasn't wired before our first
  // DOMContentLoaded READY.
  const startRetryLoop = () => {
    if (retryTimer || configReceived) return;
    let attempts = 0;
    retryTimer = setInterval(() => {
      if (configReceived || attempts >= 20) {
        if (retryTimer) {
          clearInterval(retryTimer);
          retryTimer = null;
        }
        return;
      }
      attempts += 1;
      notifyReady();
    }, 250);
  };

  const bootReady = () => {
    notifyReady();
    startRetryLoop();
  };

  if (document.readyState !== 'loading') {
    bootReady();
  } else {
    window.addEventListener('DOMContentLoaded', bootReady);
  }
};
